import React, { useMemo, useContext, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import { useAgendas } from '../hooks/useAgendas';
import { useCustomStatuses, AVAILABLE_COLORS } from '../hooks/useCustomStatuses';
import AnalyticsPanel from '../components/AnalyticsPanel';
import AnalyticsKpiCard from '../components/AnalyticsKpiCard';
import AnalyticsInsightCard from '../components/AnalyticsInsightCard';

const COLORS = AVAILABLE_COLORS.map(({ value }) => value).length > 0
  ? AVAILABLE_COLORS.map(({ value }) => value)
  : ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28EFF', '#FF6F91', '#FFB347', '#B6FFB0'];

// Caché en memoria para Analytics: evita volver a leer Firestore si entrás a la pestaña en los últimos 2 min
const ANALYTICS_CACHE_TTL_MS = 2 * 60 * 1000;
const analyticsCache = { key: null, data: null, timestamp: 0 };

function isPremium(plan) {
  return plan === 'premium';
}

export default function AnalyticsPremium() {
  const { user, plan } = useContext(AuthContext);
  const { agendas } = useAgendas();
  const { allStatuses } = useCustomStatuses();
  const [allItems, setAllItems] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: 0,
    percent: 0,
  });

  const toSafeLabel = (value) => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && !Number.isNaN(value)) return String(value);
    if (value == null) return '';
    return String(value);
  };

  const toSafeNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const isValidWhatsappNumber = (phone) => {
    if (!phone) return false;
    const digits = String(phone).replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 13;
  };

  const isWhatsappAvailable = (item) => {
    if (item?.whatsappNotAvailable) return false;
    if (item?.whatsapp && String(item.whatsapp).trim()) return true;
    return isValidWhatsappNumber(item?.phone);
  };

  const normalizeText = (value) =>
    toSafeLabel(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const singularize = (word) => {
    if (word.length <= 4) return word;
    if (word.endsWith('sses') || word.endsWith('ss')) return word;
    if (word.endsWith('es')) return word.slice(0, -2);
    if (word.endsWith('s')) return word.slice(0, -1);
    return word;
  };

  const CATEGORY_STOPWORDS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'en', 'para', 'por', 'a', 'el', 'al']);
  const CATEGORY_IGNORED = new Set(['futbol']);

  const normalizeCategoryKey = (value) => {
    const normalized = normalizeText(value);
    if (!normalized) return '';
    const tokens = normalized
      .split(/\s+/)
      .map((token) => singularize(token))
      .filter((token) => token && !CATEGORY_STOPWORDS.has(token) && !CATEGORY_IGNORED.has(token));
    if (tokens.length === 0) return '';
    tokens.sort();
    return tokens.join(' ').trim();
  };

  const toTitleCase = (value) =>
    value
      .split(' ')
      .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
      .join(' ');

  const formatNumber = (value) => {
    const safeValue = toSafeNumber(value);
    return new Intl.NumberFormat('es-ES').format(safeValue);
  };

  const showTooltip = (event, payload) => {
    setTooltip({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      ...payload,
    });
  };

  const moveTooltip = (event) => {
    setTooltip((prev) => (prev.visible ? { ...prev, x: event.clientX, y: event.clientY } : prev));
  };

  const hideTooltip = () => {
    setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  };

  const renderBarList = (data, color = '#0088FE', totalOverride = null) => {
    const totalValue = totalOverride ?? data.reduce((sum, entry) => sum + toSafeNumber(entry.value), 0);
    return (
      <div className="analytics-bar-list">
        {data.map((entry, idx) => {
          const value = toSafeNumber(entry.value);
          const percent = totalValue > 0 ? Math.round((value / totalValue) * 100) : 0;
          const label = toSafeLabel(entry.name);
          const barColor = entry.color || color;
          return (
            <div
              key={`${label}-${idx}`}
              style={{ marginBottom: '0.75rem' }}
              onMouseEnter={(event) => showTooltip(event, { label, value, percent })}
              onMouseMove={moveTooltip}
              onMouseLeave={hideTooltip}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                <strong>{label}</strong>
                <span>{formatNumber(value)} ({percent}%)</span>
              </div>
              <div style={{ height: 8, background: '#e9ecef', borderRadius: 999 }}>
                <div
                  style={{
                    width: `${percent}%`,
                    height: '100%',
                    background: barColor,
                    borderRadius: 999,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLegendList = (data, showValue = false) => (
    <div className="analytics-legend-list">
      {data.map((entry, idx) => (
        <div key={`${toSafeLabel(entry.name)}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: entry.color || COLORS[idx % COLORS.length],
              display: 'inline-block',
            }}
          />
          <span>
            {toSafeLabel(entry.name)}
            {showValue ? ` (${formatNumber(entry.value)})` : ''}
          </span>
        </div>
      ))}
    </div>
  );

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const startRad = (Math.PI / 180) * startAngle;
    const endRad = (Math.PI / 180) * endAngle;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  const renderPieSvg = (data) => {
    const totalValue = data.reduce((sum, entry) => sum + toSafeNumber(entry.value), 0);
    if (totalValue <= 0) {
      return <p>No hay datos para mostrar.</p>;
    }

    const size = 260;
    const cx = size / 2;
    const cy = size / 2;
    const r = 100;
    let startAngle = -90;

    return (
      <svg className="analytics-pie" viewBox={`0 0 ${size} ${size}`} role="img">
        {data.map((entry, idx) => {
          const value = toSafeNumber(entry.value);
          if (value <= 0) return null;
          const angle = (value / totalValue) * 360;
          const endAngle = startAngle + angle;
          const path = describeArc(cx, cy, r, startAngle, endAngle);
          const percent = totalValue > 0 ? Math.round((value / totalValue) * 100) : 0;
          const label = toSafeLabel(entry.name);
          startAngle = endAngle;
          return (
            <path
              key={`${label}-${idx}`}
              d={path}
              fill={entry.color || COLORS[idx % COLORS.length]}
              onMouseEnter={(event) => showTooltip(event, { label, value, percent })}
              onMouseMove={moveTooltip}
              onMouseLeave={hideTooltip}
            />
          );
        })}
      </svg>
    );
  };

  useEffect(() => {
    if (!user) {
      setAllItems([]);
      setLoadError(null);
      return;
    }
    if (!Array.isArray(agendas) || agendas.length === 0) {
      setAllItems([]);
      setLoadError(null);
      return;
    }

    const cacheKey = `${user.uid}-${agendas.map((a) => a.id).sort().join(',')}`;
    const now = Date.now();
    if (
      analyticsCache.key === cacheKey &&
      analyticsCache.data &&
      now - analyticsCache.timestamp < ANALYTICS_CACHE_TTL_MS
    ) {
      setAllItems(analyticsCache.data);
      setLoadingAll(false);
      return;
    }

    let isActive = true;
    const loadAllContacts = async () => {
      setLoadingAll(true);
      setLoadError(null);
      try {
        const collected = [];
        for (const agenda of agendas) {
          const ref = agenda.id === 'main'
            ? collection(db, 'users', user.uid, 'agenda')
            : collection(db, 'users', user.uid, 'agendas', agenda.id, 'contacts');
          const snapshot = await getDocs(ref);
          snapshot.forEach((docSnap) => {
            collected.push({
              id: docSnap.id,
              ...docSnap.data(),
              agendaId: agenda.id,
              agendaName: agenda.name,
            });
          });
        }
        if (isActive) {
          setAllItems(collected);
          analyticsCache.key = cacheKey;
          analyticsCache.data = collected;
          analyticsCache.timestamp = Date.now();
        }
      } catch (err) {
        if (isActive) {
          setLoadError(err);
        }
      } finally {
        if (isActive) {
          setLoadingAll(false);
        }
      }
    };

    loadAllContacts();
    return () => {
      isActive = false;
    };
  }, [user, agendas]);

  const safeItems = Array.isArray(allItems) ? allItems : [];
  const total = safeItems.length;

  const toDateSafe = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  // Validaciones robustas para evitar crash
  const filterOptions = useMemo(() => {
    const cityCounts = {};
    const categoryCounts = {};
    const statusCounts = {};
    const categoryLabels = {};

    safeItems.forEach((item) => {
      const city = toSafeLabel(item?.city).trim();
      const rawCategory = toSafeLabel(item?.category).trim();
      const categoryKey = normalizeCategoryKey(rawCategory);
      const status = toSafeLabel(item?.status || 'pendiente').trim();

      if (city) cityCounts[city] = (cityCounts[city] || 0) + 1;
      if (categoryKey) {
        categoryCounts[categoryKey] = (categoryCounts[categoryKey] || 0) + 1;
        if (!categoryLabels[categoryKey]) {
          categoryLabels[categoryKey] = toTitleCase(categoryKey);
        }
      }
      if (status) statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return {
      cities: Object.keys(cityCounts).sort().map((city) => ({
        value: city,
        label: `${city} (${cityCounts[city]})`,
        count: cityCounts[city],
      })),
      categories: Object.keys(categoryCounts).sort().map((category) => ({
        value: category,
        label: `${categoryLabels[category] || category} (${categoryCounts[category]})`,
        count: categoryCounts[category],
      })),
      statuses: Object.keys(statusCounts).sort().map((status) => ({
        value: status,
        label: `${status} (${statusCounts[status]})`,
        count: statusCounts[status],
      })),
    };
  }, [safeItems]);

  const safeCities = Array.isArray(filterOptions?.cities) ? filterOptions.cities : [];
  const safeCategories = Array.isArray(filterOptions?.categories) ? filterOptions.categories : [];
  const safeStatuses = Array.isArray(filterOptions?.statuses) ? filterOptions.statuses : [];

  const cityData = useMemo(
    () =>
      safeCities.length > 0
        ? safeCities
            .map((c) => ({ name: toSafeLabel(c.value), value: toSafeNumber(c.count) }))
            .filter((c) => c.name && c.value > 0)
        : [],
    [safeCities]
  );
  const categoryData = useMemo(
    () =>
      safeCategories.length > 0
        ? safeCategories
            .map((c) => ({ name: toSafeLabel(c.value), value: toSafeNumber(c.count) }))
            .filter((c) => c.name && c.value > 0)
        : [],
    [safeCategories]
  );
  const statusMeta = useMemo(() => {
    const metaById = {};
    const metaByName = {};
    allStatuses.forEach((status) => {
      const idKey = toSafeLabel(status.id).trim().toLowerCase();
      const nameKey = toSafeLabel(status.name).trim().toLowerCase();
      metaById[idKey] = status;
      metaByName[nameKey] = status;
    });
    return { metaById, metaByName };
  }, [allStatuses]);

  const statusData = useMemo(
    () =>
      safeStatuses.length > 0
        ? safeStatuses
            .map((s) => {
              const raw = toSafeLabel(s.value);
              const normalized = raw.trim().toLowerCase();
              const meta = statusMeta.metaById[normalized] || statusMeta.metaByName[normalized];
              const label = meta ? `${meta.emoji} ${meta.name}` : raw;
              return {
                name: label,
                value: toSafeNumber(s.count),
                color: meta?.color,
                emoji: meta?.emoji,
              };
            })
            .filter((s) => s.name && s.value > 0)
        : [],
    [safeStatuses, statusMeta]
  );

  const completenessData = useMemo(() => {
    const emailCount = safeItems.filter((i) => i?.email && i.email !== 'N/A').length;
    const phoneCount = safeItems.filter((i) => i?.phone && String(i.phone).trim()).length;
    const whatsappCount = safeItems.filter((i) => isWhatsappAvailable(i)).length;
    const websiteCount = safeItems.filter((i) => i?.website && String(i.website).trim()).length;
    return [
      { name: 'Email', value: emailCount },
      { name: 'Teléfono', value: phoneCount },
      { name: 'WhatsApp', value: whatsappCount },
      { name: 'Website', value: websiteCount },
    ];
  }, [safeItems]);

  const completionScore = useMemo(() => {
    if (safeItems.length === 0) return 0;
    const fields = ['email', 'phone', 'whatsapp', 'website', 'address', 'city', 'category'];
    const totalFields = fields.length;
    const filled = safeItems.reduce((sum, item) => {
      const count = fields.reduce((acc, field) => {
        if (field === 'whatsapp') {
          return acc + (isWhatsappAvailable(item) ? 1 : 0);
        }
        const value = item?.[field];
        return acc + (value && String(value).trim() ? 1 : 0);
      }, 0);
      return sum + count;
    }, 0);
    return Math.round((filled / (safeItems.length * totalFields)) * 100);
  }, [safeItems]);

  const recentCounts = useMemo(() => {
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const last7 = now.getTime() - 7 * dayMs;
    const last14 = now.getTime() - 14 * dayMs;
    const last30 = now.getTime() - 30 * dayMs;
    const last90 = now.getTime() - 90 * dayMs;
    let count7 = 0;
    let countPrev7 = 0;
    let count30 = 0;
    let count90 = 0;
    safeItems.forEach((item) => {
      const date = toDateSafe(item?.createdAt);
      if (!date) return;
      const time = date.getTime();
      if (time >= last7) {
        count7 += 1;
      } else if (time >= last14) {
        countPrev7 += 1;
      }
      if (time >= last30) count30 += 1;
      if (time >= last90) count90 += 1;
    });
    return { count7, countPrev7, count30, count90 };
  }, [safeItems]);

  const agendaData = useMemo(() => {
    const agendaCounts = {};
    safeItems.forEach((item) => {
      const name = toSafeLabel(item?.agendaName || 'Sin agenda');
      agendaCounts[name] = (agendaCounts[name] || 0) + 1;
    });
    return Object.keys(agendaCounts)
      .sort()
      .map((name) => ({ name, value: agendaCounts[name] }));
  }, [safeItems]);

  const agendaCategoryData = useMemo(() => {
    const agendaCategorySets = {};
    safeItems.forEach((item) => {
      const agendaName = toSafeLabel(item?.agendaName || 'Sin agenda');
      const category = toSafeLabel(item?.category).trim();
      if (!agendaCategorySets[agendaName]) {
        agendaCategorySets[agendaName] = new Set();
      }
      if (category) {
        agendaCategorySets[agendaName].add(category);
      }
    });
    return Object.keys(agendaCategorySets)
      .sort()
      .map((name) => ({ name, value: agendaCategorySets[name].size }))
      .filter((entry) => entry.value > 0);
  }, [safeItems]);

  const agendaCityData = useMemo(() => {
    const agendaCitySets = {};
    safeItems.forEach((item) => {
      const agendaName = toSafeLabel(item?.agendaName || 'Sin agenda');
      const city = toSafeLabel(item?.city).trim();
      if (!agendaCitySets[agendaName]) {
        agendaCitySets[agendaName] = new Set();
      }
      if (city) {
        agendaCitySets[agendaName].add(city);
      }
    });
    return Object.keys(agendaCitySets)
      .sort()
      .map((name) => ({ name, value: agendaCitySets[name].size }))
      .filter((entry) => entry.value > 0);
  }, [safeItems]);

  const topCities = useMemo(
    () => [...cityData].sort((a, b) => b.value - a.value).slice(0, 5),
    [cityData]
  );
  const topCategories = useMemo(
    () => [...categoryData].sort((a, b) => b.value - a.value).slice(0, 5),
    [categoryData]
  );
  const topStatuses = useMemo(
    () => [...statusData].sort((a, b) => b.value - a.value).slice(0, 5),
    [statusData]
  );

  const missingFieldsData = useMemo(() => {
    const fields = [
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Teléfono' },
      { key: 'whatsapp', label: 'WhatsApp' },
      { key: 'website', label: 'Website' },
      { key: 'address', label: 'Dirección' },
      { key: 'city', label: 'Ciudad' },
      { key: 'category', label: 'Categoría' },
    ];

    return fields.map((field) => {
      if (field.key === 'whatsapp') {
        const missingCount = safeItems.filter((item) => !isWhatsappAvailable(item)).length;
        return { name: field.label, value: missingCount };
      }
      const missingCount = safeItems.filter((item) => !item?.[field.key] || !String(item[field.key]).trim()).length;
      return { name: field.label, value: missingCount };
    });
  }, [safeItems]);

  const missingFieldsTop = useMemo(
    () => [...missingFieldsData].sort((a, b) => b.value - a.value).slice(0, 5),
    [missingFieldsData]
  );

  const recencyData = useMemo(() => {
    const bucket7 = recentCounts.count7;
    const bucket30 = Math.max(recentCounts.count30 - recentCounts.count7, 0);
    const bucket90 = Math.max(recentCounts.count90 - recentCounts.count30, 0);
    const bucketOlder = Math.max(total - recentCounts.count90, 0);
    return [
      { name: '0-7 días', value: bucket7 },
      { name: '8-30 días', value: bucket30 },
      { name: '31-90 días', value: bucket90 },
      { name: '90+ días', value: bucketOlder },
    ];
  }, [recentCounts, total]);

  const growthDelta = useMemo(() => {
    if (recentCounts.countPrev7 === 0) {
      return recentCounts.count7 > 0 ? 100 : 0;
    }
    const delta = ((recentCounts.count7 - recentCounts.countPrev7) / recentCounts.countPrev7) * 100;
    return Math.round(delta);
  }, [recentCounts]);

  const managedRate = useMemo(() => {
    if (total === 0) return 0;
    const pendingValues = new Set(['pendiente', 'pending', 'sin estado']);
    const managedCount = safeItems.filter((item) => {
      const status = toSafeLabel(item?.status || 'pendiente').trim().toLowerCase();
      return status && !pendingValues.has(status);
    }).length;
    return Math.round((managedCount / total) * 100);
  }, [safeItems, total]);

  const insights = [];
  if (total === 0) {
    insights.push({
      title: 'Sin datos suficientes',
      description: 'Agrega contactos para desbloquear comparativos, tendencias y rankings.',
      tone: 'neutral',
    });
  } else {
    if (growthDelta >= 20) {
      insights.push({
        title: 'Crecimiento acelerado',
        description: `La base creció ${growthDelta}% vs la semana anterior.`,
        tone: 'positive',
      });
    } else if (growthDelta <= -10) {
      insights.push({
        title: 'Ritmo de captación a la baja',
        description: `La captación cayó ${Math.abs(growthDelta)}% vs la semana anterior.`,
        tone: 'warning',
      });
    } else {
      insights.push({
        title: 'Crecimiento estable',
        description: 'El ritmo semanal se mantiene estable respecto a la semana anterior.',
        tone: 'neutral',
      });
    }

    if (missingFieldsTop[0]?.value > 0) {
      insights.push({
        title: 'Campos críticos por completar',
        description: `Prioriza ${toSafeLabel(missingFieldsTop[0].name)} y ${toSafeLabel(missingFieldsTop[1]?.name || missingFieldsTop[0].name)}.`,
        tone: 'warning',
      });
    }

    if (topCategories[0]) {
      insights.push({
        title: 'Categoría líder',
        description: `${toSafeLabel(topCategories[0].name)} concentra ${formatNumber(topCategories[0].value)} contactos.`,
        tone: 'positive',
      });
    }

    if (topCities[0]) {
      insights.push({
        title: 'Ciudad con mayor volumen',
        description: `${toSafeLabel(topCities[0].name)} aporta ${formatNumber(topCities[0].value)} contactos.`,
        tone: 'neutral',
      });
    }
  }

  // Validaciones para evitar errores de renderizado
  const safeCityData = Array.isArray(topCities) ? topCities : [];
  const safeAllCityData = Array.isArray(cityData) ? cityData : [];
  const safeCategoryData = Array.isArray(topCategories) ? topCategories : [];
  const safeAllCategoryData = Array.isArray(categoryData) ? categoryData : [];
  const safeStatusData = Array.isArray(topStatuses) ? topStatuses : [];
  const safeCompletenessData = Array.isArray(completenessData) ? completenessData : [];
  const safeAgendaData = Array.isArray(agendaData) ? agendaData : [];

  if (!isPremium(plan)) {
    return (
      <div className="analytics-premium-page">
        <h1>📊 Analytics</h1>
        <div className="premium-required">
          <h2>🔒 Debes ser premium para ver Analytics</h2>
          <p>Esta sección está disponible únicamente para usuarios con plan Premium.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-premium-page">
      {loadingAll && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Cargando datos de analytics...</p>
        </div>
      )}
      <div className="analytics-layout">
        <div className="analytics-column analytics-column--main">
          <section className="analytics-kpi-grid analytics-kpi-grid--compact">
            <AnalyticsKpiCard label="Contactos totales" value={formatNumber(total)} subtitle="Base completa" />
            <AnalyticsKpiCard label="Nuevos 7 días" value={formatNumber(recentCounts.count7)} subtitle="Última semana" />
            <AnalyticsKpiCard
              label="Crecimiento semanal"
              value={`${growthDelta > 0 ? '+' : ''}${growthDelta}%`}
              subtitle="Vs. semana anterior"
            />
            <AnalyticsKpiCard label="Contactos gestionados" value={`${managedRate}%`} subtitle="Estado activo" />
          </section>

          <section className="analytics-kpi-grid analytics-kpi-grid--secondary">
            <AnalyticsKpiCard label="Nuevos 30 días" value={formatNumber(recentCounts.count30)} subtitle="Último mes" />
            <AnalyticsKpiCard label="Calidad de datos" value={`${completionScore}%`} subtitle="Campos completos" />
          </section>

          <section className="analytics-insights-grid analytics-insights-grid--compact">
            {insights.map((insight, idx) => (
              <AnalyticsInsightCard
                key={`${insight.title}-${idx}`}
                title={insight.title}
                description={insight.description}
                tone={insight.tone}
              />
            ))}
          </section>
        </div>

        <div className="analytics-column analytics-column--charts">
          <section className="analytics-panels-grid analytics-panels-grid--compact">
            <AnalyticsPanel
              title="Distribución por Categoría"
              subtitle="Todas las categorías"
              footer={safeAllCategoryData.length > 0 ? renderLegendList(safeAllCategoryData, true) : null}
            >
              {safeAllCategoryData.length > 0 ? renderPieSvg(safeAllCategoryData) : <p>No hay datos para mostrar.</p>}
            </AnalyticsPanel>

            <AnalyticsPanel
              title="Distribución por ciudades"
              subtitle="Todas las ciudades"
              footer={safeAllCityData.length > 0 ? renderLegendList(safeAllCityData, true) : null}
            >
              {safeAllCityData.length > 0 ? renderPieSvg(safeAllCityData) : <p>No hay datos para mostrar.</p>}
            </AnalyticsPanel>

            <AnalyticsPanel
              title="Distribución por Estado"
              subtitle="Seguimiento operativo"
            >
              {safeStatusData.length > 0
                ? renderBarList(safeStatusData, '#FFBB28')
                : <p>No hay datos para mostrar.</p>}
            </AnalyticsPanel>

            <AnalyticsPanel title="Completitud de Datos" subtitle="Campos críticos">
              {safeCompletenessData.length > 0
                ? renderBarList(safeCompletenessData, '#FF8042', total)
                : <p>No hay datos para mostrar.</p>}
            </AnalyticsPanel>

            <AnalyticsPanel
              title="Antigüedad de Contactos"
              subtitle="Frescura del pipeline"
              footer={recencyData.length > 0 ? renderLegendList(recencyData, true) : null}
            >
              {recencyData.length > 0 ? renderPieSvg(recencyData) : <p>No hay datos para mostrar.</p>}
            </AnalyticsPanel>

          </section>
        </div>
      </div>

      {tooltip.visible && (
        <div
          className="analytics-tooltip"
          style={{ left: `${tooltip.x + 12}px`, top: `${tooltip.y + 12}px` }}
        >
          <div className="analytics-tooltip-label">{tooltip.label}</div>
          <div className="analytics-tooltip-value">
            {formatNumber(tooltip.value)} · {tooltip.percent}%
          </div>
        </div>
      )}
    </div>
  );
}

